/**
 * Voice dictation for the scribe's Dictate mode, through the clinic's Azure
 * Speech resource (BAA-covered) — never the browser's built-in recognition.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { aiErrorMessage } from "@/api/ai.api";
import { createLiveTranscriber, type LiveTranscriber } from "@/lib/azure-conversation-transcriber";
import { buildScribePhrases } from "@/lib/scribe-phrases";

export function VoiceDictateButton({
  onText,
  onError,
}: {
  onText: (text: string) => void;
  onError: (message: string) => void;
}) {
  const [state, setState] = useState<"idle" | "starting" | "listening">("idle");
  const transcriberRef = useRef<LiveTranscriber | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stop = async () => {
    const t = transcriberRef.current;
    transcriberRef.current = null;
    if (t) await t.stop();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setState("idle");
  };

  const start = async () => {
    setState("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
      streamRef.current = stream;
      const transcriber = await createLiveTranscriber(stream, {
        onPhrase: (line) => onTextRef.current(line.text),
        onInterim: () => {},
        onError: (message) => {
          onError(message);
          void stop();
        },
      }, buildScribePhrases());
      await transcriber.start();
      transcriberRef.current = transcriber;
      setState("listening");
    } catch (err) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setState("idle");
      onError(aiErrorMessage(err, err instanceof Error ? err.message : "Couldn't start voice dictation."));
    }
  };

  useEffect(
    () => () => {
      void transcriberRef.current?.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  if (state === "listening") {
    return (
      <Button variant="outline" size="sm" className="gap-1.5 border-rose-300 text-rose-700" onClick={() => void stop()}>
        <Square className="h-3 w-3 fill-current" />
        Stop dictating
      </Button>
    );
  }
  return (
    <Button variant="outline" size="sm" className="gap-1.5" disabled={state === "starting"} onClick={() => void start()}>
      {state === "starting" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
      Dictate by voice
    </Button>
  );
}
